import { motion } from "framer-motion";
import { useState } from "react";
import { SEO } from "@/components/SEO";
import { Play, Clock, Video } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { VIDEOS_DATA } from "@/lib/index";
import { IMAGES } from "@/assets/images";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 300, damping: 35 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 300, damping: 35 },
};

const typeLabels = {
  presentation: 'Présentation',
  temoignage: 'Témoignage',
  conseil: 'Conseil',
};

const typeColors = {
  presentation: 'bg-primary/10 text-primary',
  temoignage: 'bg-secondary/10 text-secondary',
  conseil: 'bg-accent/10 text-accent-foreground',
};

export default function Videos() {
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const filteredVideos = selectedType
    ? VIDEOS_DATA.filter(video => video.type === selectedType)
    : VIDEOS_DATA;

  const types = Array.from(new Set(VIDEOS_DATA.map(v => v.type)));

  return (
    <div className="min-h-screen">
      <SEO
        title="Vidéos — Informations sur l'expertise familiale et la médiation"
        description="Regardez nos vidéos explicatives sur la médiation familiale, l'expertise psychosociale et la coparentalité. Ressources visuelles pour mieux comprendre nos services en Outaouais."
        keywords="vidéos médiation familiale Québec, explications expertise psychosociale, coparentalité vidéo, ressources audiovisuelles famille"
        canonical="/videos"
      />
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.CONSULTATION_6}
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div variants={staggerItem}>
              <Badge className="mb-4">Vidéos</Badge>
            </motion.div>
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              variants={staggerItem}
            >
              Bibliothèque vidéo
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground max-w-3xl mx-auto"
              variants={staggerItem}
            >
              Découvrez nos vidéos de présentation, témoignages et conseils pratiques. Une façon visuelle et accessible de mieux comprendre nos services et approches.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            className="flex flex-wrap gap-3 justify-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
          >
            <Button
              variant={selectedType === null ? "default" : "outline"}
              onClick={() => setSelectedType(null)}
            >
              <Video className="mr-2 h-4 w-4" />
              Toutes les vidéos
            </Button>
            {types.map((type) => (
              <Button
                key={type}
                variant={selectedType === type ? "default" : "outline"}
                onClick={() => setSelectedType(type)}
              >
                {typeLabels[type as keyof typeof typeLabels]}
              </Button>
            ))}
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {filteredVideos.map((video) => {
              const thumbnailImage = IMAGES[video.thumbnail as keyof typeof IMAGES];
              return (
                <motion.div key={video.id} variants={staggerItem}>
                  <Card className="h-full hover:shadow-lg transition-all duration-200 group overflow-hidden">
                    <CardContent className="p-0">
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={thumbnailImage}
                          alt={video.titre}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Play className="h-8 w-8 text-primary-foreground ml-1" />
                          </div>
                        </div>
                        <div className="absolute top-4 right-4">
                          <Badge className={typeColors[video.type as keyof typeof typeColors]}>
                            {typeLabels[video.type as keyof typeof typeLabels]}
                          </Badge>
                        </div>
                        <div className="absolute bottom-4 right-4">
                          <Badge variant="secondary" className="bg-black/60 text-white">
                            <Clock className="h-3 w-3 mr-1" />
                            {video.duree}
                          </Badge>
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                          {video.titre}
                        </h3>
                        <p className="text-muted-foreground mb-4 line-clamp-2">
                          {video.description}
                        </p>
                        <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <Play className="mr-2 h-4 w-4" />
                          Regarder
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
          >
            <h2 className="text-3xl font-bold mb-4">Vous souhaitez en savoir plus?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Nos vidéos vous donnent un aperçu de notre approche. Pour une consultation personnalisée, n'hésitez pas à nous contacter.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <a href="#/contact">Prendre rendez-vous</a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="#/services">Découvrir nos services</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
